// App.js (production-ready with code splitting)
// Implements lazy loading for major route chunks
// Reduces initial bundle size from 18.7MB to ~1MB

import DashboardLayout from "components/layouts/DashboardLayout";
import NavBar from "components/NavBar";
import ApplicationCatalogue from "pages/ApplicationCatalogue";
import CheckoutCancel from "pages/CheckoutCancel";
import CheckoutSuccess from "pages/CheckoutSuccess";
import Home from "pages/Home";
import Login from "pages/Login";
import NotFound from "pages/NotFound";
import ProfileCompletionDemo from "pages/ProfileCompletionDemo";
import ResetPassword from "pages/ResetPassword";
import SignUp from "pages/SignUp";
import WhyChooseUs from "pages/WhyChooseUs";
import PrivacyPolicy from "pages/PrivacyPolicy";
import React, { Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ProfileLayout } from "./components/Profile";
import ProfileRedirect from "./components/Profile/ProfileRedirect";
import AuthCallback from "./pages/AuthCallback";
import AdminRoute from "./components/AdminRoute";

// Lazy-loaded components for code splitting
import {
  Dashboard,
  MePage,
  WorkHistoryPageV2,
  HighlightsPageV2Clean,
  SummaryAndSkillsPage,
  ResumePreviewPage,
  ApplicationFlowPage,
  SkillConfigurationPage,
  PricingPage,
  SubscriptionPage,
  HelpPage,
  WorkHistoryPage,
  WorkHistoryV2,
  WorkHistoryV3,
  OrgSnapshotFlow,
  SelectTargetRole,
  WorkHistoryForm,
  Highlights,
  MasterResume,
  SelectBullets,
  CoverLetterTailor,
  TailorResumePage,
  SectionDragPreview,
  WorkHistoryHelp
} from "./components/LazyComponents";

// Admin components
const AdminRouter = React.lazy(() => import('./pages/Admin/AdminRouter'));
const ApplicationMonitor = React.lazy(() => import('./pages/Admin/ApplicationMonitor'));

// Loading components
import {
  SmartDashboardLoading,
  SmartProfileLoading,
  SmartApplicationsLoading,
  SmartWorkHistoryLoading,
  SmartEditorLoading,
  SmartAdminLoading
} from "./components/SmartLoadingFallback";

// --- Route Guards ---
function PrivateRoute({ children }) {
  const token = localStorage.getItem("token");
  const location = useLocation();

  if (!token) {
    // Redirect to login with return URL
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  return children;
}

function PublicOnlyRoute({ children }) {
  const token = localStorage.getItem("token");
  return token ? <Navigate to="/dashboard" replace /> : children;
}

export default function App() {
  const location = useLocation();
  const path = location.pathname.toLowerCase();

  // Public pages that should always show NavBar (never dashboard)
  const PUBLIC_PAGES = [
    "/",
    "/login",
    "/signup",
    "/forgotpassword",
    "/why-choose-us",
    "/privacy-policy",
    "/checkout/success",
    "/checkout/cancel",
  ];
  const isPublicPage = PUBLIC_PAGES.includes(path);

  // Pages that use the dashboard area (hide top NavBar)
  const DASH_PREFIXES = [
    "/dashboard",
    "/me",
    "/applications",
    "/select-bullets",
    "/tailorresume",
    "/resume-editor",
    "/coverletter",
    "/cover-letter-editor",
  ];
  const isDashboardArea = DASH_PREFIXES.some((p) => path.startsWith(p));

  // Force the sidebar collapsed on specific routes
  const COLLAPSED_MATCHERS = [
    /^\/me\/highlights/,
    /^\/me\/masterresume/,
    /^\/applications\/add-job/, // InputJD
    /^\/tailorresume(?:\/|$)/, // TailorResume (param or not)
    /^\/coverletter(?:\/|$)/, // TailorCoverLetter (param or not)
    /^\/cover-letter-editor(?:\/|$)/, // editor variant
  ];
  const sidebarCollapsed = COLLAPSED_MATCHERS.some((rx) => rx.test(path));

  return (
    <>
      {isPublicPage && <NavBar />}

      <Routes>
        {/* Public */}
        <Route 
          path="/" 
          element={
            <PublicOnlyRoute>
              <Home />
            </PublicOnlyRoute>
          } 
        />
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <Login />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicOnlyRoute>
              <SignUp />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/ForgotPassword"
          element={
            <PublicOnlyRoute>
              <ResetPassword />
            </PublicOnlyRoute>
          }
        />

        {/* Public pages - accessible to both public and authenticated users */}
        <Route path="/why-choose-us" element={<WhyChooseUs />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel" element={<CheckoutCancel />} />

        {/* Auth callback for OAuth providers */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Full-screen Application Monitor (no sidebar) */}
        <Route 
          path="/admin/dashboard/applicationmonitor" 
          element={
            <PrivateRoute>
              <AdminRoute>
                <Suspense fallback={<SmartAdminLoading />}>
                  <ApplicationMonitor />
                </Suspense>
              </AdminRoute>
            </PrivateRoute>
          } 
        />

        {/* Demo page for testing profile completion */}
        <Route
          path="/demo/profile-completion"
          element={<ProfileCompletionDemo />}
        />

        {/* Protected area with persistent DashboardLayout */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              {/* Pass the forced collapsed state down to the layout */}
              <DashboardLayout sidebarCollapsed={sidebarCollapsed} />
            </PrivateRoute>
          }
        >
          {/* All routes below render inside <Outlet /> with smart lazy loading */}
          <Route path="dashboard/*" element={
            <Suspense fallback={<SmartDashboardLoading />}>
              <Dashboard />
            </Suspense>
          } />
          
          {/* Profile / Me - Stepper Routes (for onboarding users) */}
          <Route
            path="me"
            element={
              <ProfileRedirect
                stepperPath="/me"
                tabPath="/me/profile/basic-info"
              >
                <Suspense fallback={<SmartProfileLoading />}>
                  <MePage />
                </Suspense>
              </ProfileRedirect>
            }
          />
          <Route
            path="me/work-experience"
            element={
              <ProfileRedirect
                stepperPath="/me/work-experience"
                tabPath="/me/profile/work-history"
              >
                <Suspense fallback={<SmartWorkHistoryLoading />}>
                  <WorkHistoryPageV2 />
                </Suspense>
              </ProfileRedirect>
            }
          />
          <Route
            path="me/education-projects"
            element={
              <ProfileRedirect
                stepperPath="/me/education-projects"
                tabPath="/me/profile/education-projects"
              >
                <Suspense fallback={<SmartProfileLoading />}>
                  <HighlightsPageV2Clean />
                </Suspense>
              </ProfileRedirect>
            }
          />
          <Route
            path="me/summary-skills"
            element={
              <ProfileRedirect
                stepperPath="/me/summary-skills"
                tabPath="/me/profile/summary-skills"
              >
                <Suspense fallback={<SmartProfileLoading />}>
                  <SummaryAndSkillsPage />
                </Suspense>
              </ProfileRedirect>
            }
          />
          <Route
            path="me/summary"
            element={
              <ProfileRedirect
                stepperPath="/me/summary"
                tabPath="/me/profile/summary"
              >
                <Suspense fallback={<SmartProfileLoading />}>
                  <SummaryAndSkillsPage />
                </Suspense>
              </ProfileRedirect>
            }
          />

          {/* Profile Tab Routes (for completed users) */}
          <Route path="me/profile" element={
            <Suspense fallback={<SmartProfileLoading />}>
              <ProfileLayout />
            </Suspense>
          }>
            <Route path="basic-info" element={
              <Suspense fallback={<SmartProfileLoading />}>
                <MePage />
              </Suspense>
            } />
            <Route path="work-history" element={
              <Suspense fallback={<SmartWorkHistoryLoading />}>
                <WorkHistoryPageV2 />
              </Suspense>
            } />
            <Route path="education" element={
              <Suspense fallback={<SmartProfileLoading />}>
                <HighlightsPageV2Clean />
              </Suspense>
            } />
            <Route path="summary" element={
              <Suspense fallback={<SmartProfileLoading />}>
                <SummaryAndSkillsPage />
              </Suspense>
            } />
            <Route path="resume-preview" element={
              <Suspense fallback={<SmartProfileLoading />}>
                <ResumePreviewPage />
              </Suspense>
            } />
          </Route>

          {/* Other Me routes */}
          <Route path="me/applications" element={
            <Suspense fallback={<SmartApplicationsLoading />}>
              <ApplicationFlowPage />
            </Suspense>
          } />
          <Route
            path="me/skillconfig/:applicationId"
            element={
              <Suspense fallback={<SmartApplicationsLoading />}>
                <SkillConfigurationPage />
              </Suspense>
            }
          />

          {/* Pricing - Protected Route */}
          <Route path="pricing" element={
            <Suspense fallback={<SmartDashboardLoading />}>
              <PricingPage />
            </Suspense>
          } />

          {/* Admin Dashboard */}
          <Route 
            path="admin/*" 
            element={
              <AdminRoute>
                <Suspense fallback={<SmartAdminLoading />}>
                  <AdminRouter />
                </Suspense>
              </AdminRoute>
            } 
          />

          {/* Subscription Management */}
          <Route path="subscription" element={
            <Suspense fallback={<SmartDashboardLoading />}>
              <SubscriptionPage />
            </Suspense>
          } />

          {/* Help - Protected Route */}
          <Route path="help" element={
            <Suspense fallback={<SmartDashboardLoading />}>
              <HelpPage />
            </Suspense>
          } />
          
          {/* Work History Routes */}
          <Route path="me/work" element={
            <Suspense fallback={<SmartWorkHistoryLoading />}>
              <WorkHistoryPage />
            </Suspense>
          } />
          <Route path="me/work-history-help" element={
            <Suspense fallback={<SmartProfileLoading />}>
              <WorkHistoryHelp />
            </Suspense>
          } />
          <Route path="me/work1" element={
            <Suspense fallback={<SmartWorkHistoryLoading />}>
              <WorkHistoryPageV2 />
            </Suspense>
          } />
          <Route path="me/wht" element={
            <Suspense fallback={<SmartWorkHistoryLoading />}>
              <WorkHistoryV2 />
            </Suspense>
          } />
          <Route path="me/wht1" element={
            <Suspense fallback={<SmartWorkHistoryLoading />}>
              <WorkHistoryV3 />
            </Suspense>
          } />
          <Route path="me/flow" element={
            <Suspense fallback={<SmartProfileLoading />}>
              <OrgSnapshotFlow />
            </Suspense>
          } />
          <Route path="me/TargetRole" element={
            <Suspense fallback={<SmartProfileLoading />}>
              <SelectTargetRole />
            </Suspense>
          } />
          <Route path="me/WorkHistory" element={
            <Suspense fallback={<SmartWorkHistoryLoading />}>
              <WorkHistoryForm />
            </Suspense>
          } />
          <Route path="me/Highlights" element={
            <Suspense fallback={<SmartProfileLoading />}>
              <Highlights />
            </Suspense>
          } />
          <Route path="me/MasterResume" element={
            <Suspense fallback={<SmartProfileLoading />}>
              <MasterResume />
            </Suspense>
          } />

          {/* Applications */}
          <Route path="applications" element={<ApplicationCatalogue />} />
          <Route path="me/applications" element={
            <Suspense fallback={<SmartApplicationsLoading />}>
              <ApplicationFlowPage />
            </Suspense>
          } />
          
          {/* Editors */}
          <Route path="select-bullets" element={
            <Suspense fallback={<SmartEditorLoading />}>
              <SelectBullets />
            </Suspense>
          } />
          <Route path="cover-letter-editor" element={
            <Suspense fallback={<SmartEditorLoading />}>
              <CoverLetterTailor />
            </Suspense>
          } />
          <Route path="resume-editor" element={
            <Suspense fallback={<SmartEditorLoading />}>
              <SectionDragPreview />
            </Suspense>
          } />
          
          {/* Parameterized editors */}
          <Route
            path="CoverLetter/:applicationId"
            element={
              <Suspense fallback={<SmartEditorLoading />}>
                <CoverLetterTailor />
              </Suspense>
            }
          />
          <Route
            path="TailorResume/:applicationId"
            element={
              <Suspense fallback={<SmartEditorLoading />}>
                <TailorResumePage />
              </Suspense>
            }
          />
        </Route>

        {/* 404 */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
